import conversationBackground from 'mastodon/../images/theme/bg_light.png';
import PropTypes from 'prop-types';
import { PureComponent } from 'react';
import { connect } from 'react-redux';
import api from 'mastodon/api';
import { me } from 'mastodon/initial_state';
import { buildConversationDocument, koreanDate } from 'mastodon/utils/conversation_save';

const mapDispatchToProps = dispatch => ({
  request: (url, params) => dispatch((_dispatch, getState) => api(getState).get(url, { params })),
});

class ConversationSave extends PureComponent {
  static propTypes = { request: PropTypes.func.isRequired };
  state = { mode: 'public', date: koreanDate(), query: '', accounts: [], account: null, rows: null, busy: false, searching: false, error: '', progress: 0, active: -1 };
  cache = new Map();
  generation = 0;
  alive = true;
  searchGeneration = 0;
  searchTimer = null;
  composing = false;

  componentWillUnmount () { this.alive = false; this.generation += 1; this.searchGeneration += 1; clearTimeout(this.searchTimer); }

  change = values => { this.generation += 1; this.setState({ ...values, rows: null, error: '', busy: false }); };

  queryChanged = query => {
    clearTimeout(this.searchTimer);
    this.searchGeneration += 1;
    this.change({ query, account: null, accounts: [], active: -1, searching: false });
    if (query.trim() && !this.composing) this.searchTimer = setTimeout(this.search, 350);
  };

  chooseAccount = account => {
    clearTimeout(this.searchTimer);
    this.searchGeneration += 1;
    this.change({ account, query: `${account.display_name || account.username} (@${account.acct})`, accounts: [], active: -1, searching: false });
  };

  searchKey = event => {
    if (event.nativeEvent.isComposing || this.composing) return;
    const { accounts, active } = this.state;
    if (event.key === 'Escape') {
      clearTimeout(this.searchTimer);
      this.searchGeneration += 1;
      this.setState({ accounts: [], active: -1, searching: false });
    } else if (accounts.length && ['ArrowDown', 'ArrowUp'].includes(event.key)) {
      event.preventDefault();
      this.setState({ active: event.key === 'ArrowDown' ? (active + 1) % accounts.length : (active <= 0 ? accounts.length - 1 : active - 1) });
    } else if (event.key === 'Enter') {
      event.preventDefault();
      if (active >= 0 && accounts[active]) this.chooseAccount(accounts[active]);
    }
  };

  search = async () => {
    const query = this.state.query.trim();
    if (!query) return;
    const generation = ++this.searchGeneration;
    this.setState({ searching: true, error: '' });
    try {
      const { data } = await this.props.request('/api/v1/accounts/search', { q: query, resolve: false, limit: 8 });
      const accounts = data.filter(a => a.id !== me);
      if (this.alive && generation === this.searchGeneration) this.setState({ accounts, active: -1, error: accounts.length ? '' : '찾은 캐릭터가 없어요.' });
    } catch { if (this.alive && generation === this.searchGeneration) this.setState({ error: '캐릭터를 찾지 못했어요. 다시 입력해 주세요.' }); }
    finally { if (this.alive && generation === this.searchGeneration) this.setState({ searching: false }); }
  };

  preview = async () => {
    const { mode, date, account } = this.state;
    if (!date || (mode === 'character' && !account)) { this.setState({ error: '날짜와 캐릭터를 선택해 주세요.' }); return; }
    const popup = window.open('', '_blank');
    if (!popup) { this.setState({ error: '팝업을 허용하면 미리보기를 열 수 있어요.' }); return; }
    popup.opener = null;
    popup.document.title = '대화 불러오는 중';
    popup.document.body.textContent = '대화를 불러오는 중…';
    const snapshot = { mode, date, account };
    const key = `${mode}/${date}/${account?.id || ''}`;
    if (this.cache.has(key)) { this.showPreview(popup, { ...snapshot, rows: this.cache.get(key) }); this.setState({ error: '' }); return; }
    const generation = ++this.generation;
    this.setState({ busy: true, rows: null, error: '', progress: 0 });
    const rows = [];
    let after;
    try {
      do {
        const { data } = await this.props.request('/api/v1/conversation_exports', { mode, date, account_id: account?.id, after_id: after });
        if (!this.alive || generation !== this.generation || popup.closed) { if (!popup.closed) popup.close(); if (this.alive && generation === this.generation) this.setState({ busy: false }); return; }
        rows.push(...data.statuses);
        if (data.next_id && data.next_id === after) throw new Error('pagination');
        after = data.next_id;
        this.setState({ progress: rows.length });
      } while (after);
      this.cache.set(key, rows);
      if (this.cache.size > 3) this.cache.delete(this.cache.keys().next().value);
      this.setState({ busy: false });
      this.showPreview(popup, { ...snapshot, rows });
    } catch { if (!popup.closed) popup.document.body.textContent = '불러오기를 완료하지 못했어요. 원래 화면에서 다시 시도해 주세요.'; if (this.alive && generation === this.generation) this.setState({ busy: false, error: '불러오기를 완료하지 못했어요. 다시 시도해 주세요.' }); }
  };

  showPreview = (popup, snapshot) => {
    if (popup.closed) return;
    popup.document.open();
    popup.document.write(buildConversationDocument(snapshot, document.querySelector('meta[name=style-nonce]')?.content || '', new URL(conversationBackground, window.location.href).href));
    popup.document.close();
    const printButton = popup.document.getElementById('save-pdf');
    printButton.addEventListener('click', async () => {
      await Promise.race([
        Promise.all(Array.from(popup.document.images, img => img.decode().catch(() => {}))),
        new Promise(resolve => setTimeout(resolve, 5000)),
      ]);
      popup.print();
    });
  };

  render () {
    const { mode, date, query, accounts, account, busy, searching, error, progress, active } = this.state;
    return (
      <section className='conversation-save' aria-label='대화 저장하기'>
        <h3><img className='conversation-save__heading-icon' src='/rose-favicon-32.png' width='18' height='18' alt='' aria-hidden='true' />대화 저장하기</h3>
        <div className='conversation-save__tabs'>
          <button type='button' aria-pressed={mode === 'public'} onClick={() => this.change({ mode: 'public' })}>퍼블릭</button>
          <button type='button' aria-pressed={mode === 'character'} onClick={() => this.change({ mode: 'character' })}>캐릭터 대화</button>
        </div>
        <label className='conversation-save__date'>날짜 <small>한국 시간 기준</small><input type='date' value={date} max={koreanDate()} onChange={e => this.change({ date: e.target.value })} /></label>
        {mode === 'character' && <div className='conversation-save__character'>
          <label>상대 캐릭터<input type='text' role='combobox' aria-autocomplete='list' aria-expanded={accounts.length > 0} aria-controls='conversation-save-candidates' aria-activedescendant={active >= 0 ? `conversation-save-option-${active}` : undefined} autoComplete='off' value={query} placeholder='닉네임 또는 아이디' onChange={e => this.queryChanged(e.target.value)} onKeyDown={this.searchKey} onCompositionStart={() => { this.composing = true; clearTimeout(this.searchTimer); this.searchGeneration += 1; }} onCompositionEnd={e => { this.composing = false; this.queryChanged(e.target.value); }} /></label>
          {searching && <p role='status' className='conversation-save__hint'>검색 중…</p>}
          <div id='conversation-save-candidates' role='listbox' aria-label='캐릭터 검색 결과' hidden={!accounts.length}>
            {accounts.map((a, index) => <button type='button' role='option' id={`conversation-save-option-${index}`} aria-selected={active === index} key={a.id} onClick={() => this.chooseAccount(a)}><img src={a.avatar_static || a.avatar} alt='' width='32' height='32' loading='lazy' /><span><strong>{a.display_name || a.username}</strong><small>@{a.acct}</small></span></button>)}
          </div>
          {account && <div className='conversation-save__selected'><img src={account.avatar_static || account.avatar} alt='' width='32' height='32' /><span>{account.display_name || account.username}</span></div>}
        </div>}
        <button className='conversation-save__primary' type='button' disabled={busy} onClick={this.preview}>{busy ? `${progress}개 불러오는 중…` : '미리보기'}</button>
        {busy && <button type='button' onClick={() => this.change({})}>취소</button>}
        {error && <p role='alert'>{error}</p>}

      </section>
    );
  }
}

export default connect(null, mapDispatchToProps)(ConversationSave);
